// The wake's shader: one material for every ship's foam tufts (wakeGeometry).
//
// Each tuft is born at the stern, travels back along the ship's track, spreads
// out along its arm and dissolves — all on the GPU. The pass is transparent and
// unlit, so foam brightens white over the sea and dims to a cold grey at night.
// The soft round edge is computed from the quad's corner, so the wake needs no
// texture at all.
//
// The wave term is the same formula as the water surface (TerrainWater) and the
// ship geometry's own bob (trafficMaterial), driven by the shared clocks, so the
// foam rides the real waterline instead of floating over it.

import * as THREE from 'three';
import { waterTime } from '../TerrainWater.js';
import { trafficNight, trafficTime } from './trafficMaterial.js';

/** Seconds for one foam tuft to travel the whole trail. */
const CYCLE = 3.2;

export const wakeMaterial = new THREE.MeshBasicMaterial({
  color: '#ffffff',
  transparent: true,
  depthWrite: false,
  // Both ends of the trail dissolve; blending normal alpha keeps the foam from
  // glowing the way additive blending would over a bright day-time sea.
  fog: true,
});

wakeMaterial.onBeforeCompile = (shader) => {
  shader.uniforms.uTime = trafficTime;
  shader.uniforms.uWave = waterTime;
  shader.uniforms.uNight = trafficNight;
  shader.vertexShader = `uniform float uTime;
uniform float uWave;
attribute vec2 aCorner;
attribute vec4 aTuft;
// aTint is the ship's (sail r, g, b, phase); the phase de-synchronises the
// wakes so ships in adjacent lanes do not breathe in lockstep.
attribute vec4 aTint;
varying vec2 vCorner;
varying vec2 vWorld;
varying float vFade;
float _vhash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
${shader.vertexShader}`.replace(
    '#include <begin_vertex>',
    `#include <begin_vertex>
      float age = fract(uTime / ${CYCLE.toFixed(2)} + aTuft.x + aTint.w);
      float side = aTuft.y;
      // Per-tuft randoms, stable for the life of the buffer (aTuft.x is unique).
      float j1 = _vhash(vec2(aTuft.x * 91.7, 3.1));
      float j2 = _vhash(vec2(aTuft.x * 57.3, 7.7));
      // A tuft widens and brightens as it ages, then thins out as it dissolves.
      float grow = 0.6 + age * 1.3;
      // Scatter every tuft along the track and across the wake. Without this the
      // arms are two ruled lines: neighbouring tufts line up toe-to-tail and the
      // foam reads as straight white bars (owner's note, 2026-10-22).
      float back = 0.3 + age * 0.7 + (j2 - 0.5) * 0.26;
      float spread = (0.55 + 0.9 * j1);
      float turb = sin(uTime * 2.1 + aTuft.x * 6.283 + side * 2.2) * 0.09 * age
                 + sin(uTime * 3.7 + age * 9.0) * 0.03 * age;
      // Round-ish patches: an elongated tuft fuses with its neighbours.
      vec2 c = aCorner * aTuft.w * grow * vec2(1.15, 0.95);
      vec3 p = vec3(
        -back + c.x,
        0.0,
        side * (0.05 + age * aTuft.z) * spread + (j1 - 0.5) * 0.14 + turb + c.y
      );
      // Ride the same wave as the water surface (TerrainWater / trafficMaterial).
      vec2 wp = instanceMatrix[3].xz;
      float w = sin(wp.x * 2.5 + uWave) * cos(wp.y * 2.5 + uWave * 0.7);
      w += 0.6 * sin(wp.x * 5.0 + uWave * 1.3) * sin(wp.y * 5.0 - uWave * 0.9);
      w += 0.35 * sin((wp.x + wp.y) * 1.1 + uWave * 0.5);
      p.y = 0.03 + w * 0.05;
      transformed = p;
      // World position, for the noise that breaks the foam into streaks.
      vWorld = (modelMatrix * instanceMatrix * vec4(p, 1.0)).xz;
      vCorner = aCorner;
      // Fade in fast, hold, then dissolve over the back half of the trail.
      float life = smoothstep(0.0, 0.08, age) * (1.0 - smoothstep(0.75, 1.0, age));
      vFade = life * (abs(side) > 0.5 ? 0.9 : 0.45);`,
  );
  shader.fragmentShader = `uniform float uTime;
uniform float uNight;
varying vec2 vCorner;
varying vec2 vWorld;
varying float vFade;
float _whash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
float _wnoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(_whash(i), _whash(i + vec2(1.0, 0.0)), u.x),
             mix(_whash(i + vec2(0.0, 1.0)), _whash(i + vec2(1.0, 1.0)), u.x), u.y);
}
${shader.fragmentShader}`.replace(
    '#include <color_fragment>',
    `#include <color_fragment>
    // Soft edge, no texture: radial falloff across the quad.
    float _soft = 1.0 - smoothstep(0.5, 1.0, length(vCorner) * 2.0);
    // Churned foam, not a smooth blob: two scrolling octaves of value noise at
    // world scale break the patch into streaks and holes (the Pirate Quest
    // wake does the same thing on its ribbon).
    float _n = _wnoise(vWorld * 1.7 + vec2(uTime * 0.35, -uTime * 0.2)) * 0.6
             + _wnoise(vWorld * 4.1 - vec2(uTime * 0.5, 0.0)) * 0.4;
    float _streak = smoothstep(0.3, 0.72, _n);
    // Bright foam by day, a cold grey by night.
    diffuseColor.rgb = mix(vec3(0.78, 0.91, 0.94), vec3(0.30, 0.38, 0.44), uNight);
    diffuseColor.a *= _soft * vFade * (0.55 + 0.55 * _streak);`,
  );
};
wakeMaterial.customProgramCacheKey = () => 'sea-wake';
